import type { RouteLocationNormalizedLoaded, Router } from 'vue-router'

import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  SITE_LOCALE,
  SITE_NAME,
  getSiteUrl,
  toAbsoluteUrl,
} from '@/modules/common/seo/site'

export interface SeoPayload {
  title?: string
  description?: string
  image?: string
  canonical?: string
  robots?: string
  type?: string
  structuredData?: Record<string, unknown> | null
}

function ensureMetaTag(key: string, value: string, attribute: 'name' | 'property' = 'name'): void {
  if (typeof document === 'undefined') {
    return
  }

  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`) as HTMLMetaElement | null
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attribute, key)
    document.head.appendChild(tag)
  }

  tag.setAttribute('content', value)
}

function ensureLinkTag(rel: string, href: string): void {
  if (typeof document === 'undefined') {
    return
  }

  let tag = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (!tag) {
    tag = document.createElement('link')
    tag.setAttribute('rel', rel)
    document.head.appendChild(tag)
  }

  tag.setAttribute('href', href)
}

function ensureStructuredData(data: Record<string, unknown> | null): void {
  if (typeof document === 'undefined') {
    return
  }

  const id = 'labactive-seo-jsonld'
  let tag = document.head.querySelector(`#${id}`) as HTMLScriptElement | null

  if (!data) {
    tag?.remove()
    return
  }

  if (!tag) {
    tag = document.createElement('script')
    tag.id = id
    tag.type = 'application/ld+json'
    document.head.appendChild(tag)
  }

  tag.textContent = JSON.stringify(data)
}

function formatTitle(title?: string): string {
  const normalized = title?.trim()

  if (!normalized) {
    return DEFAULT_TITLE
  }

  if (normalized.includes(SITE_NAME)) {
    return normalized
  }

  return `${normalized} | ${SITE_NAME}`
}

function normalizeDescription(description?: string): string {
  const normalized = description?.trim()
  if (!normalized) {
    return DEFAULT_DESCRIPTION
  }

  return normalized.length > 170 ? `${normalized.slice(0, 167).trim()}...` : normalized
}

function buildCanonicalFromRoute(route: RouteLocationNormalizedLoaded): string {
  const rawCanonical = route.meta['canonical']
  if (typeof rawCanonical === 'string' && rawCanonical.trim() !== '') {
    return toAbsoluteUrl(rawCanonical)
  }

  const canonicalQuery = Array.isArray(route.meta['canonicalQuery'])
    ? (route.meta['canonicalQuery'] as string[])
    : []

  const searchParams = new URLSearchParams()
  const routeQuery = route.query as Record<string, unknown>

  for (const key of canonicalQuery) {
    const value = routeQuery[key]
    if (typeof value === 'string' && value.trim() !== '') {
      searchParams.set(key, value)
      continue
    }

    if (Array.isArray(value)) {
      const normalized = value.find((entry) => typeof entry === 'string' && entry.trim() !== '')
      if (typeof normalized === 'string') {
        searchParams.set(key, normalized)
      }
    }
  }

  const query = searchParams.toString()
  const path = query === '' ? route.path : `${route.path}?${query}`

  return toAbsoluteUrl(path)
}

function shouldIndexRoute(route: RouteLocationNormalizedLoaded): boolean {
  if (route.meta['index'] === false) {
    return false
  }

  if (
    route.meta['requiresAuth'] ||
    route.meta['requiresAdmin'] ||
    route.meta['requiresSupplierAuth'] ||
    route.meta['requiresGuest'] ||
    route.meta['requiresSupplierGuest']
  ) {
    return false
  }

  return true
}

export function applySeo(payload: SeoPayload): void {
  if (typeof document === 'undefined') {
    return
  }

  const title = formatTitle(payload.title)
  const description = normalizeDescription(payload.description)
  const canonical =
    typeof payload.canonical === 'string' && payload.canonical.trim() !== ''
      ? toAbsoluteUrl(payload.canonical)
      : typeof window !== 'undefined'
        ? window.location.href
        : getSiteUrl()
  const image = toAbsoluteUrl(payload.image ?? DEFAULT_OG_IMAGE)
  const robots =
    payload.robots ?? 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
  const type = payload.type ?? 'website'

  document.title = title

  ensureMetaTag('description', description)
  ensureMetaTag('robots', robots)
  ensureMetaTag('og:title', title, 'property')
  ensureMetaTag('og:description', description, 'property')
  ensureMetaTag('og:type', type, 'property')
  ensureMetaTag('og:url', canonical, 'property')
  ensureMetaTag('og:image', image, 'property')
  ensureMetaTag('og:site_name', SITE_NAME, 'property')
  ensureMetaTag('og:locale', SITE_LOCALE, 'property')
  ensureMetaTag('twitter:card', 'summary_large_image')
  ensureMetaTag('twitter:title', title)
  ensureMetaTag('twitter:description', description)
  ensureMetaTag('twitter:image', image)
  ensureLinkTag('canonical', canonical)
  ensureStructuredData(payload.structuredData ?? null)
}

export function applyRouteSeo(route: RouteLocationNormalizedLoaded): void {
  const title = typeof route.meta['title'] === 'string' ? route.meta['title'] : DEFAULT_TITLE
  const description =
    typeof route.meta['description'] === 'string' ? route.meta['description'] : DEFAULT_DESCRIPTION
  const ogImage =
    typeof route.meta['ogImage'] === 'string' ? route.meta['ogImage'] : DEFAULT_OG_IMAGE
  const ogType = typeof route.meta['ogType'] === 'string' ? route.meta['ogType'] : 'website'
  const robots =
    typeof route.meta['robots'] === 'string'
      ? route.meta['robots']
      : shouldIndexRoute(route)
        ? undefined
        : 'noindex,nofollow'
  const structuredDataMeta = route.meta['structuredData']
  const structuredData =
    structuredDataMeta === 'home'
      ? buildHomeStructuredData()
      : structuredDataMeta === 'organization'
        ? buildOrganizationStructuredData()
        : structuredDataMeta && typeof structuredDataMeta === 'object'
          ? (structuredDataMeta as Record<string, unknown>)
          : null

  const payload: SeoPayload = {
    title,
    description,
    image: ogImage,
    type: ogType,
    canonical: buildCanonicalFromRoute(route),
    structuredData,
  }

  if (typeof robots === 'string') {
    payload.robots = robots
  }

  applySeo(payload)
}

export function setupRouterSeo(router: Router): void {
  router.afterEach((to) => {
    applyRouteSeo(to)
  })

  router.isReady().then(() => {
    applyRouteSeo(router.currentRoute.value)
  })
}

export function buildOrganizationStructuredData(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: getSiteUrl(),
    logo: toAbsoluteUrl('/logo.svg'),
  }
}

export function buildHomeStructuredData(): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      buildOrganizationStructuredData(),
      {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: getSiteUrl(),
        potentialAction: {
          '@type': 'SearchAction',
          target: `${getSiteUrl()}/catalog?search={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  }
}
